/**
 * Cobros de Amazon del banco ↔ lo apuntado en _Pedidos (puro). Amazon cobra al enviar: cada cobro es el total de un envío
 * (o de varios), así que se busca por importe exacto en unos días alrededor, ignorando la cuenta (los emails no dicen
 * qué tarjeta). Si un pedido no tiene email de envío, vale su total. Cuando dos cobros pueden ser del mismo envío, o un
 * cobro tiene varias opciones, no se elige solo: se pregunta.
 * Las devoluciones (ingresos de Amazon en el banco) se buscan igual entre los emails de reembolso, que llegan antes que el
 * dinero; cada artículo devuelto vuelve a la categoría en la que se compró, así que resta de ese gasto.
 * El reparto propone una categoría por artículo: la que le diste la última vez a ese artículo; si no, la de una regla
 * cuyo texto esté en su nombre; si no, la del cobro. Sin IA: los nombres no salen del dispositivo.
 */
import { categorize, normalizeText, round2, DIVIDED, type Rule, type Category } from '@pf/domain';
import type { Command, OrderKind, OrderRow, Workbook, WorkbookMovement } from '@pf/workbook';

export interface OrderItem { item: string; qty: number; amount: number | null }
/** Lo de un email de _Pedidos (una Ref): un envío o un pedido, con sus artículos. */
export interface OrderGroup { ref: string; kind: OrderKind; orderId: string; date: string; total: number | null; items: OrderItem[]; movementId: string }

export interface AmazonCharge {
  movement: WorkbookMovement;
  /** Lo que ya tiene enlazado (vacío si no). */
  linked: OrderGroup[];
  /** Combinaciones de envíos que suman su importe (la primera, la más probable). */
  options: OrderGroup[][];
  /** Una sola opción y nadie más la quiere: se puede proponer sin preguntar cuál. */
  sure: boolean;
}

export const isAmazonCharge = (concept: string) => /AMAZON|AMZN/i.test(concept);

const day = (d: string) => Date.parse(`${d}T00:00:00Z`) / 86400000;
/**
 * Días que puede haber entre el email y el movimiento: el envío llega el mismo día o poco antes; un pedido, hasta un mes
 * antes; el reembolso, hasta dos semanas antes de que el banco lo devuelva.
 */
const WINDOW: Record<OrderKind, [number, number]> = { shipment: [-7, 3], order: [-30, 3], refund: [-14, 3] };
const MAX_SET = 4, MAX_CANDIDATES = 12, MAX_OPTIONS = 5;

export function orderGroups(orders: OrderRow[]): OrderGroup[] {
  const by = new Map<string, OrderGroup>();
  for (const o of orders) {
    if (!o.ref) continue;
    const g = by.get(o.ref) ?? by.set(o.ref, { ref: o.ref, kind: o.kind, orderId: o.orderId, date: o.date, total: o.total, items: [], movementId: o.movementId }).get(o.ref)!;
    g.items.push({ item: o.item, qty: o.qty, amount: o.amount });
    if (o.movementId) g.movementId = o.movementId;
  }
  return [...by.values()];
}

/** Subconjuntos (hasta MAX_SET) de candidatos que suman exactamente el importe. */
function sums(cands: OrderGroup[], target: number): OrderGroup[][] {
  const out: OrderGroup[][] = [];
  const walk = (from: number, picked: OrderGroup[], sum: number) => {
    if (out.length >= 50) return;
    if (picked.length && Math.abs(sum - target) < 0.011) { out.push(picked); return; }
    if (picked.length >= MAX_SET || sum > target + 0.011) return;
    for (let i = from; i < cands.length; i++) walk(i + 1, [...picked, cands[i]!], round2(sum + cands[i]!.total!));
  };
  walk(0, [], 0);
  return out;
}

/**
 * Los cobros de Amazon (gastos) y sus devoluciones (ingresos) desde unos días antes del primer email apuntado, con lo que
 * tienen enlazado o sus opciones: envíos o pedidos para un cobro, reembolsos para una devolución.
 */
export function amazonCharges(wb: Pick<Workbook, 'movements' | 'orders'>): AmazonCharge[] {
  const groups = orderGroups(wb.orders ?? []);
  if (!groups.length) return [];
  const ids = new Set(wb.movements.map(m => m.id).filter(Boolean));
  const linkedTo = (g: OrderGroup) => (g.movementId && ids.has(g.movementId) ? g.movementId : '');
  const shipped = new Set(groups.filter(g => g.kind === 'shipment').map(g => g.orderId));
  // Libres: envíos y reembolsos sin movimiento, y pedidos sin ningún email de envío (si lo hay, el cobro es el del envío)
  const free = groups.filter(g => !linkedTo(g) && (g.total ?? 0) > 0 && (g.kind !== 'order' || !shipped.has(g.orderId)));
  const first = Math.min(...groups.map(g => day(g.date)));
  const charges = wb.movements.filter(m => m.id && m.amount !== 0 && isAmazonCharge(m.concept) && day(m.date) >= first - 3);

  const out = charges.map((m): AmazonCharge => {
    const linked = groups.filter(g => linkedTo(g) === m.id);
    if (linked.length) return { movement: m, linked, options: [], sure: true };
    const d = day(m.date), target = Math.abs(m.amount);
    const dist = (g: OrderGroup) => Math.abs(day(g.date) - d);
    const fits = (g: OrderGroup) => (m.amount > 0) === (g.kind === 'refund');
    const cands = free.filter(g => { const [a, b] = WINDOW[g.kind]; const x = day(g.date) - d; return fits(g) && x >= a && x <= b && g.total! <= target + 0.011; })
      .sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'shipment' ? -1 : 1) || dist(a) - dist(b)).slice(0, MAX_CANDIDATES);
    const score = (set: OrderGroup[]) => set.length * 100 + set.filter(g => g.kind === 'order').length * 10 + Math.max(...set.map(dist));
    const options = sums(cands, target).sort((a, b) => score(a) - score(b)).slice(0, MAX_OPTIONS);
    return { movement: m, linked: [], options, sure: false };
  });
  // Segura: una sola opción y ningún otro cobro sin enlazar puede usar esos mismos emails
  const wanted = new Map<string, number>();
  for (const c of out) for (const ref of new Set(c.options.flat().map(g => g.ref))) wanted.set(ref, (wanted.get(ref) ?? 0) + 1);
  for (const c of out) if (!c.linked.length) c.sure = c.options.length === 1 && c.options[0]!.every(g => wanted.get(g.ref) === 1);
  return out.sort((a, b) => b.movement.date.localeCompare(a.movement.date));
}

/** Cobros (y devoluciones) con su email encontrado y aún sin repartir. */
export const pendingCharges = (wb: Pick<Workbook, 'movements' | 'orders'>) => amazonCharges(wb).filter(c => !c.linked.length && c.options.length);

/** El aviso de Inicio para lo pendiente (importes de los movimientos): «2 cobros y 1 devolución de Amazon…»; '' si no hay. */
export function amazonNotice(amounts: number[]): string {
  const n = amounts.filter(a => a < 0).length, r = amounts.length - n;
  if (!amounts.length) return '';
  const what = [n ? `${n} cobro${n === 1 ? '' : 's'}` : '', r ? (r === 1 ? '1 devolución' : `${r} devoluciones`) : ''].filter(Boolean).join(' y ');
  const it = amounts.length > 1 ? 'repártelos' : n ? 'repártelo' : 'repártela';
  return `${what} de Amazon con su ${r && !n ? 'reembolso' : n && !r ? 'pedido' : 'email'} encontrado: ${it} por artículos`;
}

const key = (item: string) => normalizeText(item);

/** Lo que aprendiste: la categoría que diste a cada artículo en cobros ya repartidos (lo último manda). */
export function learnedCategories(wb: Pick<Workbook, 'movements' | 'orders' | 'parts'>): Map<string, string> {
  const learned = new Map<string, string>();
  const byId = new Map(wb.movements.map(m => [m.id, m]));
  const linked = (wb.orders ?? []).filter(o => o.movementId && byId.has(o.movementId))
    .sort((a, b) => byId.get(a.movementId)!.date.localeCompare(byId.get(b.movementId)!.date));
  for (const o of linked) { const c = itemCategory(wb, byId.get(o.movementId)!, o.item); if (c) learned.set(key(o.item), c); }
  return learned;
}

/** La categoría que tiene un artículo en su movimiento: la del movimiento o, si está dividido, la de la parte que lo nombra. */
function itemCategory(wb: Pick<Workbook, 'parts'>, m: WorkbookMovement, item: string): string {
  if (m.category !== DIVIDED) return m.category;
  return wb.parts.find(p => p.movementId === m.id && p.note.split(' · ').some(n => key(n.replace(/ ×\d+$/, '')) === key(item)))?.category ?? '';
}

/** El mismo artículo con otro nombre: Amazon lo acorta distinto en cada email («Impresora de fotos - 2nd...»). */
const sameItem = (a: string, b: string) => {
  const x = key(a.replace(/(\.{3}|…)\s*$/, '')), y = key(b.replace(/(\.{3}|…)\s*$/, ''));
  return !!x && !!y && (x.startsWith(y) || y.startsWith(x));
};

/**
 * Lo devuelto, en la compra: el artículo del mismo pedido en un cobro ya enlazado, con su categoría y su importe (si se
 * devuelven menos unidades, en proporción). Si no se reconoce el artículo pero el cobro tenía una sola categoría, esa.
 */
function originalOf(wb: Pick<Workbook, 'movements' | 'orders' | 'parts'>, orderId: string, x: OrderItem): { category: string; amount: number | null } | null {
  const byId = new Map(wb.movements.map(m => [m.id, m]));
  const bought = (wb.orders ?? []).filter(o => o.orderId === orderId && o.kind !== 'refund' && byId.has(o.movementId));
  const o = bought.find(b => sameItem(b.item, x.item));
  if (o) return { category: itemCategory(wb, byId.get(o.movementId)!, o.item), amount: o.amount === null ? null : round2(o.amount * Math.min(x.qty, o.qty) / (o.qty || 1)) };
  const cats = new Set(bought.map(b => byId.get(b.movementId)!.category));
  const [only] = cats;
  return cats.size === 1 && only && only !== DIVIDED ? { category: only, amount: null } : null;
}

export interface ProposedItem extends OrderItem { amount: number; category: string; why: 'original' | 'learned' | 'rule' | 'charge' | '' }

/**
 * Artículos del cobro con su importe (si lo de los emails no suma el cobro, la diferencia se reparte en proporción; lo que
 * no tiene precio, como la media de los demás) y la categoría propuesta. En una devolución, primero la de la compra.
 */
export function proposeItems(wb: Pick<Workbook, 'movements' | 'orders' | 'parts'> & { rules: Rule[]; categories: Category[] }, charge: WorkbookMovement, set: OrderGroup[]): ProposedItem[] {
  const target = Math.abs(charge.amount);
  const items = set.flatMap(g => g.items.map(x => ({ ...x, original: g.kind === 'refund' ? originalOf(wb, g.orderId, x) : null })));
  const known = items.map(x => x.amount ?? x.original?.amount ?? null);
  const priced = known.filter((n): n is number => n !== null);
  const avg = priced.length ? priced.reduce((t, n) => t + n, 0) / priced.length : 0;
  const raw = known.map(n => n ?? avg), sum = round2(raw.reduce((t, n) => t + n, 0));
  const scaled = raw.map(n => (sum > 0 ? round2(n * target / sum) : round2(target / items.length)));
  const diff = round2(target - scaled.reduce((t, n) => t + n, 0));
  if (diff && scaled.length) { const i = scaled.indexOf(Math.max(...scaled)); scaled[i] = round2(scaled[i]! + diff); }
  const learned = learnedCategories(wb), names = new Set(wb.categories.map(c => c.name));
  const fallback = charge.category && charge.category !== DIVIDED && names.has(charge.category) ? charge.category : '';
  return items.map(({ original, ...x }, i) => {
    if (original?.category && names.has(original.category)) return { ...x, amount: scaled[i]!, category: original.category, why: 'original' as const };
    const l = learned.get(key(x.item));
    if (l && names.has(l)) return { ...x, amount: scaled[i]!, category: l, why: 'learned' as const };
    const r = categorize(wb.rules, wb.categories, charge.account, x.item, charge.amount);
    if (!r.isDefault && r.category && names.has(r.category)) return { ...x, amount: scaled[i]!, category: r.category, why: 'rule' as const };
    return { ...x, amount: scaled[i]!, category: fallback, why: fallback ? 'charge' as const : '' as const };
  });
}

const byCategory = (items: ProposedItem[]) => {
  const byCat = new Map<string, ProposedItem[]>();
  for (const x of items) (byCat.get(x.category) ?? byCat.set(x.category, []).get(x.category)!).push(x);
  return byCat;
};
const itemLabel = (x: { item: string; qty: number }) => `${x.item}${x.qty > 1 ? ` ×${x.qty}` : ''}`;
const joined = (names: string[], max: number) => { const t = names.join(' · '); return t.length <= max ? t : `${t.slice(0, max - 1)}…`; };

/**
 * Órdenes para guardar el reparto: una parte por categoría (con los artículos en su nota) o, si todo es de una categoría,
 * esa categoría sin dividir; la nota del cobro (si no tenía) con los artículos; y el enlace de los emails con el cobro.
 */
export function chargeCommands(charge: WorkbookMovement, currentParts: Array<{ fingerprint: string }>, set: OrderGroup[], items: ProposedItem[]): Command[] {
  const byCat = byCategory(items);
  const sign = Math.sign(charge.amount) || -1;
  const cmds: Command[] = [];
  const changes: { category?: string; note?: string } = {};
  if (byCat.size >= 2) {
    const parts = [...byCat].map(([category, xs]) => ({ movementId: charge.id, category, amount: round2(sign * xs.reduce((t, x) => t + x.amount, 0)), note: joined(xs.map(itemLabel), 200) }));
    cmds.push({ type: 'save-parts', movementId: charge.id, expected: currentParts.map(p => p.fingerprint), parts });
    if (charge.category !== DIVIDED) changes.category = DIVIDED;
  } else {
    if (currentParts.length) cmds.push({ type: 'save-parts', movementId: charge.id, expected: currentParts.map(p => p.fingerprint), parts: [] });
    const only = [...byCat.keys()][0] ?? '';
    if (only !== charge.category) changes.category = only;
  }
  if (!charge.note.trim()) changes.note = itemsNote(items, charge.amount > 0);
  if (Object.keys(changes).length) cmds.push({ type: 'update-movement', id: charge.id, row: charge.row, fingerprint: charge.fingerprint, changes });
  cmds.push({ type: 'link-orders', refs: set.map(g => g.ref), movementId: charge.id });
  return cmds;
}

/** La nota del cobro con sus artículos («Devolución: …» en una devolución). */
export function itemsNote(items: ProposedItem[], refund: boolean): string {
  const prefix = refund ? 'Devolución: ' : '';
  return prefix + joined(items.map(itemLabel), 200 - prefix.length);
}

/** Los artículos por categoría (importes en positivo, con los artículos en la nota); vacío si alguno no tiene categoría. */
export function itemParts(items: ProposedItem[]): Array<{ category: string; amount: number; note: string }> {
  if (!items.length || items.some(x => !x.category)) return [];
  return [...byCategory(items)].map(([category, xs]) => ({ category, amount: round2(xs.reduce((t, x) => t + x.amount, 0)), note: joined(xs.map(itemLabel), 200) }));
}

/** Lo encontrado para una fila de la importación: su email y sus artículos, o más de una opción (se elige desde Inicio). */
export type ImportAmazon = { set: OrderGroup[]; items: ProposedItem[] } | { several: true };

/**
 * En la revisión de la importación: el email de cada fila que sea un cobro o una devolución de Amazon, buscado como si ya
 * estuviera importada, junto con lo que ya hay en el libro (un email que también puede ser de otro cobro no se propone
 * solo). Las filas aún no tienen categoría: lo que no se reconoce se queda sin ella.
 */
export function amazonForImport(wb: Pick<Workbook, 'movements' | 'orders' | 'parts'> & { rules: Rule[]; categories: Category[] }, account: string,
  rows: Array<{ key: number; date: string; amount: number; description: string }>): Map<number, ImportAmazon> {
  const out = new Map<number, ImportAmazon>();
  const mine = rows.filter(r => r.amount !== 0 && isAmazonCharge(r.description));
  if (!mine.length || !wb.orders?.length) return out;
  const idOf = (key: number) => `importar:${key}`;
  const pending = mine.map((r): WorkbookMovement => ({ id: idOf(r.key), date: r.date, concept: r.description, amount: r.amount, account, category: '',
    shared: false, group: '', note: '', month: '', row: 0, fingerprint: '' }));
  const all = { ...wb, movements: [...wb.movements, ...pending] };
  const charges = new Map(amazonCharges(all).map(c => [c.movement.id, c]));
  for (const r of mine) {
    const c = charges.get(idOf(r.key));
    if (!c?.options.length) continue;
    out.set(r.key, c.sure ? { set: c.options[0]!, items: proposeItems(all, c.movement, c.options[0]!) } : { several: true });
  }
  return out;
}
